import {useMutation, UseMutationResult} from '@tanstack/react-query';
import {useHistory} from 'react-router';
import {z as r} from 'zod';

import {appRoute} from '../../app-route';
import {getSsoLoginPagePath} from '../../page/auth/sso-login/sso-login-helper';
import {ShortLocaleNameEnum} from '../../provider/locale/locale-context-type';
import {useLocale} from '../../provider/locale/locale-hook';
import {Locale} from '../../provider/locale/localization';
import {useSnackbar} from '../../provider/snackbar/snackbar-hook';
import {useUser} from '../../provider/user/user-hook';
import {deserializeApiError, postAndDeserialize} from '../../util/api-adapter';
import {
    registerErrorSchema,
    RegisterErrorType,
    RegisterFormType,
    RegisterOptionsType,
    registerSchema,
} from '../user/user-register';

import {userManagementUrl} from './user-management-const';
import {
    useUserManagementRegistrationData,
    useUserManagementRegistrationUrlQuery,
} from './user-management-registration-data';

// An SSO user has no password: the key is left out, the backend rejects an empty one.
type UserManagementRegisterOptionsType = Omit<RegisterOptionsType, 'password'> & {
    jwtToken: string | null;
    password?: string;
};

const userManagementRegisterSchema = registerSchema.extend({
    // True when the backend has opened a session: the SSO provider confirmed this user right before the form.
    isLoggedIn: r.boolean().optional(),
});

type UserManagementRegisterType = r.infer<typeof userManagementRegisterSchema>;

const url = `${userManagementUrl}/register/`;

function register(
    options: UserManagementRegisterOptionsType,
    shortLocaleName: ShortLocaleNameEnum
): Promise<UserManagementRegisterType> {
    return postAndDeserialize(url, userManagementRegisterSchema, options, {unauthorized: true, shortLocaleName});
}

export function useUserManagementRegisterMutation(): UseMutationResult<
    UserManagementRegisterType,
    unknown,
    RegisterFormType
> {
    const {jwtToken} = useUserManagementRegistrationUrlQuery();
    const {data: registrationData} = useUserManagementRegistrationData();
    const ssoClientCode = registrationData?.isSso ? registrationData.ssoClientCode : null;

    // Without a session an SSO user signs in through the identity provider once more.
    const signInPath = ssoClientCode ? getSsoLoginPagePath(ssoClientCode) : appRoute.login.path;
    const signInHintLangKey = ssoClientCode
        ? 'PAGE__REGISTER__NOTIFICATION__SUCCESS__CONTENT__SSO'
        : 'PAGE__REGISTER__NOTIFICATION__SUCCESS__CONTENT__NO_CONFIRMATION';

    const {getUser} = useUser();
    const {push} = useHistory();
    const {snackbar} = useSnackbar();
    const {shortLocaleName} = useLocale();

    function mutate(options: RegisterFormType) {
        const {password, ...restOptions} = options;

        return register(
            {
                ...restOptions,
                ...(registrationData?.isSso ? {} : {password: password.trim()}),
                jwtToken,
                firstName: options.firstName.trim(),
                lastName: options.lastName?.trim() ?? '',
                email: options.email.trim(),
                phone: options.phone.trim(),
                language: shortLocaleName,
            },
            shortLocaleName
        );
    }

    async function onSuccess(data: UserManagementRegisterType) {
        const user = data.isLoggedIn ? await getUser() : null;

        if (user && !(user instanceof Error)) {
            snackbar.success(<Locale stringKey="PAGE__REGISTER__NOTIFICATION__SUCCESS__HEADER" />);
            push(appRoute.root.path);

            return;
        }

        snackbar.success({
            message: <Locale stringKey="PAGE__REGISTER__NOTIFICATION__SUCCESS__HEADER" />,
            description: <Locale stringKey={signInHintLangKey} />,
        });

        push(signInPath);
    }

    function onError(error: unknown) {
        const apiError = deserializeApiError<RegisterErrorType>(url, registerErrorSchema, error);

        if (apiError?.firstName?.[0] || apiError?.lastName?.[0] || apiError?.email?.[0] || apiError?.phone?.[0]) {
            return;
        }

        snackbar.error({
            message: <Locale stringKey="PAGE__REGISTER__NOTIFICATION__ERROR__HEADER" />,
            description: <Locale stringKey="SNACKBAR__ERROR__TECH_SUPPORT" />,
        });
    }

    return useMutation(mutate, {onSuccess, onError});
}
